import { getActionTypeLabel } from './actionTypesRegistry';
import { resolveRelativeDate } from '@/lib/dateResolution';

/**
 * Outbound action text processing utilities
 * Cleans up verbose AI language and creates professional business summaries
 */

interface CallAction {
  id: string;
  type: string;
  phrase: string | null;
  action_data: any;
  processed: boolean;
  created_at: string;
  campaign?: {
    name: string;
    type?: string;
    businessContext?: {
      company: string;
      industry: string;
      context: string;
    };
  };
}

interface ActionSummary {
  summary: string;
  details?: string;
  urgencyText?: string;
}

/**
 * Creates a clean, professional summary of actions taken during the call
 */
export function summarizeOutboundActions(actions: CallAction[]): ActionSummary {
  const followupAction = actions.find(a => a.type === 'follow_up_call');
  const consentAction = actions.find(a => a.type === 'consent_callback');
  const emailAction = actions.find(a => a.type === 'email_provided' || a.type === 'email_promise');
  const appointmentAction = actions.find(a => a.type === 'appointment_interest' || a.type === 'appointment_booked');
  const messageAction = actions.find(a => a.type === 'message_taking');

  const parts = [];
  let details = '';

  if (emailAction) {
    parts.push(getActionTypeLabel(emailAction.type) || 'Email info request');
    const email = emailAction.action_data?.email;
    if (email) {
      details += `Email: ${email}. `;
    }
  }

  if (followupAction) {
    parts.push(getActionTypeLabel(followupAction.type) || 'Follow-up call');
  }

  if (consentAction) {
    parts.push(getActionTypeLabel(consentAction.type) || 'Callback consent given');
  }

  if (appointmentAction) {
    const appointmentLabel = getActionTypeLabel(appointmentAction.type) || 'Appointment';
    
    // For appointment_booked, try to resolve the date
    if (appointmentAction.type === 'appointment_booked') {
      const dateFromPhrase = extractAppointmentDetails(appointmentAction.phrase || '');
      const baseDate = appointmentAction.created_at ? new Date(appointmentAction.created_at) : new Date();
      
      if (dateFromPhrase) {
        const resolvedDate = resolveRelativeDate(dateFromPhrase, baseDate);
        parts.push(`${appointmentLabel} (${resolvedDate})`);
      } else {
        parts.push(appointmentLabel);
      }
    } else {
      parts.push(appointmentLabel);
    }
  }

  if (messageAction) {
    parts.push(getActionTypeLabel(messageAction.type) || 'Message to relay');
    if (messageAction.phrase) {
      details += `Message: ${cleanPhrase(messageAction.phrase)}. `;
    }
  }

  // Build summary with parts joined by " + "
  const summary = parts.length > 0 ? parts.join(' + ') : 'Contact made';

  // Extract urgency indicators
  const urgencyText = extractUrgencyFromActions(actions);

  return {
    summary,
    details: details.trim() || undefined,
    urgencyText
  };
}

/**
 * Extracts appointment timing information from phrases
 */
function extractAppointmentDetails(phrase: string): string | null {
  if (!phrase) return null;

  // Enhanced patterns to handle times with and without AM/PM
  const timePatterns = [
    // Day + time with AM/PM
    /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+at\s+(\d{1,2}(?::\d{2})?\s*(?:am|pm))/i,
    // Day + time without AM/PM  
    /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+at\s+(\d{1,2}(?::\d{2})?)(?!\s*(?:am|pm))/i,
    // Tomorrow + time with AM/PM
    /\btomorrow\s+(?:at\s+)?(\d{1,2}(?::\d{2})?\s*(?:am|pm))/i,
    // Tomorrow + time without AM/PM
    /\btomorrow\s+(?:at\s+)?(\d{1,2}(?::\d{2})?)(?!\s*(?:am|pm))/i,
    // Relative day references
    /\bnext\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i,
    /\bthis\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i,
    // Standalone time with AM/PM
    /\b(\d{1,2}(?::\d{2})?\s*(?:am|pm))/i
  ];

  for (const pattern of timePatterns) {
    const match = phrase.match(pattern);
    if (match) {
      let result = match[0].trim();
      
      // Clean up relative day references
      if (result.includes('next') || result.includes('this')) {
        result = result.replace(/\b(next|this)\s+/i, '').trim();
      }
      
      // For times without AM/PM, apply smart defaults
      if (/\d+(?::\d+)?\s*$/.test(result) && !/(am|pm)/i.test(result)) {
        const timeMatch = result.match(/(\d{1,2})(?::(\d{2}))?/);
        if (timeMatch) {
          const hour = parseInt(timeMatch[1]);
          const minute = timeMatch[2] || '00';
          // Default 1-7 to PM, others to AM
          const period = (hour >= 1 && hour <= 7) ? 'PM' : 'AM';
          result = result.replace(/(\d{1,2}(?::\d{2})?)/, `${hour}:${minute} ${period}`);
        }
      }
      
      return result;
    }
  }

  return null;
}

/**
 * Extracts urgency indicators from action phrases
 */
function extractUrgencyFromActions(actions: CallAction[]): string | null {
  const phrases = actions.map(a => a.phrase).filter(Boolean).join(' ').toLowerCase();
  
  if (phrases.includes('asap') || phrases.includes('urgent') || phrases.includes('immediately')) {
    return 'ASAP';
  }
  
  if (phrases.includes('today') || phrases.includes('this morning') || phrases.includes('this afternoon')) {
    return 'Today';
  }
  
  if (phrases.includes('tomorrow')) {
    return 'Tomorrow';
  }
  
  return null;
}

/**
 * Gets the business name that RECEIVED the call (not the caller's business)
 */
export function getRecipientBusiness(actions: CallAction[]): string | null {
  // Check each action for recipient business information
  for (const action of actions) {
    // Priority 1: Direct recipient company or captured company from action_data
    if (action.action_data?.recipient_company) {
      return action.action_data.recipient_company;
    }
    if (action.action_data?.captured_company) {
      return action.action_data.captured_company;
    }
    
    // Priority 2: Industry from action_data campaign context (recipient business context)
    if (action.action_data?.campaign?.businessContext?.industry) {
      return action.action_data.campaign.businessContext.industry;
    }
    
    // Priority 3: Industry from top-level campaign context
    if (action.campaign?.businessContext?.industry) {
      return action.campaign.businessContext.industry;
    }
  }

  // Fallback strategies for the first action
  const firstAction = actions[0];
  if (!firstAction) return null;

  // Extract business name from campaign name patterns
  const topCampaign = firstAction.campaign;
  if (topCampaign?.name) {
    const businessMatch = topCampaign.name.match(/\(([^)]+)\)/);
    if (businessMatch) {
      return businessMatch[1];
    }
  }

  // Extract from nested campaign name
  const nestedCampaign = firstAction.action_data?.campaign;
  if (nestedCampaign?.name) {
    const businessMatch = nestedCampaign.name.match(/\(([^)]+)\)/);
    if (businessMatch) {
      return businessMatch[1];
    }
  }

  return null;
}

/**
 * Clean verbose AI phrases to business format
 */
function cleanPhrase(phrase: string): string {
  if (!phrase) return '';
  
  // Remove common AI verbose patterns
  let cleaned = phrase
    .replace(/^(Great\.|Okay\.|Sure\.|Perfect\.|Absolutely\.)\s*/i, '')
    .replace(/I'll\s+/g, '')
    .replace(/\s+and\s+follow[-\s]?up\s+with\s+a\s+quick\s+call\s+later/gi, '')
    .replace(/\s+over\s+and\s+follow[-\s]?up/gi, '')
    .replace(/\s+that\s+over/gi, '')
    .trim();

  // Capitalize first letter
  if (cleaned.length > 0) {
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }

  return cleaned;
}